import { useTheme } from "@mui/material";
import { format } from "date-fns";
import { cs } from "date-fns/locale";

import {
	AdvisoryManagementResource,
	useAdvisoryManagementResource,
} from "AdvisoryManagement/Translations/AdvisoryManagementResource";
import type { InvestmentQuestionnaireResponse } from "Infrastructure/Api/Api";

type QuestionnaireStatus = 200 | 400 | 404 | 422 | undefined;

type QuestionnaireStatusResult = {
	color: string;
	tooltip: string;
	severity: "success" | "warning" | "error" | "disabled";
};

/**
 * Custom hook for handling investment questionnaire status presentation.
 *
 * Encapsulates the logic for mapping HTTP status codes to UI representation:
 * - Color coding (green/orange/red)
 * - Tooltip messages with translations
 * - Semantic severity levels
 *
 * @param status - HTTP status code from the questionnaire query (200/404/400/422)
 * @param data - Questionnaire data (only present for 200 status)
 * @param errorMessage - Error message from backend (enum value like "QuestionnaireExpired")
 * @returns Object containing color, tooltip message, and severity level
 *
 * @example
 * const { color, tooltip, severity } = useQuestionnaireStatus(
 *   questionnaireResult?.status,
 *   questionnaireResult?.data,
 *   questionnaireResult?.errorMessage
 * );
 */
export const useQuestionnaireStatus = (
	status: QuestionnaireStatus,
	data: InvestmentQuestionnaireResponse | null,
	errorMessage: string | null,
): QuestionnaireStatusResult => {
	const theme = useTheme();
	const { t } = useAdvisoryManagementResource();

	// Color mapping based on status
	const color = (() => {
		switch (status) {
			case 200:
				return "#22b518"; // success green
			case 400:
			case 422:
				return "#db781d"; // warning orange
			case 404:
				return "#c1392b"; // error red
			default:
				return theme.palette.action.disabled; // gray for undefined
		}
	})();

	// Severity mapping for semantic meaning
	const severity = (() => {
		switch (status) {
			case 200:
				return "success" as const;
			case 400:
			case 422:
				return "warning" as const;
			case 404:
				return "error" as const;
			default:
				return "disabled" as const;
		}
	})();

	// Tooltip message generation with translations
	const tooltip = (() => {
		if (!status) return "";

		switch (status) {
			case 200:
				return t(
					AdvisoryManagementResource.Shared.InvestmentQuestionnaireCompleted,
					{
						date: format(new Date(data!.createdAt), "dd.MM.yyyy", {
							locale: cs,
						}),
					},
				);
			case 404:
				return t(
					AdvisoryManagementResource.Shared.InvestmentQuestionnaireNotFound,
				);
			case 400:
			case 422: {
				// Map error message (enum value from backend) to translation key
				if (errorMessage === "QuestionnaireExpired") {
					return t(
						AdvisoryManagementResource.InvestmentQuestionnaire.Error
							.QuestionnaireExpired,
					);
				}

				// Generic error fallback
				return t(
					AdvisoryManagementResource.InvestmentQuestionnaire.Error.General,
				);
			}
			default:
				return "";
		}
	})();

	return { color, tooltip, severity };
};
